// GymBeam campaign names decorate the Magento product name with a trailing
// gift suffix: "Just Whey - GymBeam + darček", "+ PREZENTY", "+ ΔΩΡΟ". The
// gift is a campaign, not a product fact. Durable artifacts built from the
// name (images, titles) must cut it.
//
// The vocabulary is deliberately closed. Names legitimately contain "+"
// ("Vitamin C + Zinc"), so only a known gift word may be cut. One entry per
// storefront language, singular and plural, as surveyed from the mirror.
export const PROMO_GIFT_WORDS = [
  "darček", // sk
  "darčeky",
  "dárek", // cs
  "dárky",
  "ajándék", // hu
  "ajándékok",
  "cadou", // ro
  "cadouri",
  "cadeau", // fr, nl
  "cadeaus",
  "cadeaux",
  "poklon", // hr, sr
  "pokloni",
  "поклон", // sr (Cyrillic)
  "поклони",
  "darilo", // sl
  "darila",
  "darili",
  "prezent", // pl
  "prezenty",
  "geschenk", // de
  "geschenke",
  "geschenken", // nl
  "gift", // en
  "gifts",
  "omaggio", // it
  "omaggi",
  "regalo", // es
  "regalos",
  "regali", // it
  "подарък", // bg
  "подаръци",
  "подарок", // ru
  "подарки",
  "подарунок", // uk
  "подарунки",
  "δώρο", // el
  "δώρα",
] as const

// Words a campaign writes NEXT TO the gift word ("+ dárek zdarma",
// "+ darček GRATIS"). Never a gift on their own — cutting "+ zdarma" alone
// would eat a legitimate claim, so a tail of only modifiers is kept.
const PROMO_GIFT_MODIFIERS = ["zdarma", "gratis", "free"] as const

/** Accent-fold for vocabulary matching: `DARČEK` → `darcek`, `ΔΩΡΟ` → `δωρο`.
 *  Case alone is not enough — Greek and Slavic data mixes accented and
 *  unaccented spellings of the same word. */
export function foldPromoToken(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase("en")
}

const GIFT_WORDS_FOLDED = new Set<string>(PROMO_GIFT_WORDS.map(foldPromoToken))
const MODIFIERS_FOLDED = new Set<string>(PROMO_GIFT_MODIFIERS.map(foldPromoToken))

/** Cut a trailing `+ <gift>` campaign suffix off a product name.
 *
 *  The tail after the name's LAST `+` is cut only when every token in it is
 *  gift vocabulary — a gift word, a modifier, or a quantity before the first
 *  gift word ("+ 2 darčeky", "+ dárek zdarma") — and at least one token is
 *  a gift word. Anything else
 *  after the `+` is product identity and stays. A name that is nothing but a
 *  gift suffix returns empty; callers already treat an empty name as missing. */
export function withoutPromoGiftSuffix(name: string): string {
  const trimmedName = name.trim()
  const plusAt = trimmedName.lastIndexOf("+")
  if (plusAt === -1) {
    return trimmedName
  }
  const tail = trimmedName.slice(plusAt + 1).replace(/[.\s]+$/u, "")
  const tokens = tail.split(/\s+/u).filter((token) => token.length > 0)
  const folded = tokens.map(foldPromoToken)
  const firstGiftAt = folded.findIndex((token) => GIFT_WORDS_FOLDED.has(token))
  const isGiftTail =
    firstGiftAt !== -1 &&
    folded.every(
      (token, index) =>
        GIFT_WORDS_FOLDED.has(token) ||
        MODIFIERS_FOLDED.has(token) ||
        (index < firstGiftAt && /^\d+[x×]?$/u.test(token))
    )
  if (!isGiftTail) {
    return trimmedName
  }

  // Magento also writes equal-quantity campaigns as `1+1 gift`, without a
  // delimiter before the first quantity. Only recognize that explicit grammar:
  // a different or missing quantity after `+` may be product identity
  // (`Formula 1+ gift`, `Protein 100+2 gifts`).
  const beforePlus = trimmedName.slice(0, plusAt)
  const quantityPrefix = beforePlus.match(/(?:^|\s)\d+$/u)
  if (quantityPrefix) {
    const quantityBeforePlus = quantityPrefix[0]?.trim()
    const quantityBeforeGift = tokens[firstGiftAt - 1]
    const isEqualQuantityCampaign =
      firstGiftAt === 1 &&
      quantityBeforePlus !== undefined &&
      quantityBeforePlus === quantityBeforeGift
    if (!isEqualQuantityCampaign) {
      return trimmedName
    }
    return trimmedName.slice(0, quantityPrefix.index).trim()
  }

  return trimmedName.slice(0, plusAt).trim()
}
